import { useEffect, type ReactNode } from 'react'

import estilos from './Modal.module.css'

type PropsModal = {
  abierto: boolean
  titulo: string
  subtitulo?: string
  /** Botones de la parte inferior. */
  pie?: ReactNode
  ancho?: boolean
  onCerrar: () => void
  children: ReactNode
}

/**
 * Ventana modal.
 *
 * Se cierra con Escape y bloquea el desplazamiento del fondo mientras está
 * abierta: sin eso, en celular la página de atrás se mueve bajo el panel y se
 * pierde el sitio donde uno estaba.
 */
export function Modal({
  abierto,
  titulo,
  subtitulo,
  pie,
  ancho = false,
  onCerrar,
  children,
}: PropsModal) {
  useEffect(() => {
    if (!abierto) return

    function alPresionar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') onCerrar()
    }

    const desplazamientoPrevio = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', alPresionar)

    return () => {
      document.body.style.overflow = desplazamientoPrevio
      document.removeEventListener('keydown', alPresionar)
    }
  }, [abierto, onCerrar])

  if (!abierto) return null

  return (
    <div
      className={estilos.velo}
      // Cerrar al tocar por fuera, pero solo el fondo: un clic que empieza
      // dentro del panel y termina fuera no debe cerrar el formulario.
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget) onCerrar()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
    >
      <div className={`${estilos.panel} ${ancho ? estilos.ancho : ''}`}>
        <header className={estilos.cabecera}>
          <div>
            <h2 className={estilos.titulo}>{titulo}</h2>
            {subtitulo && <p className={estilos.subtitulo}>{subtitulo}</p>}
          </div>

          <button type="button" className={estilos.cerrar} onClick={onCerrar} aria-label="Cerrar">
            ✕
          </button>
        </header>

        <div className={`${estilos.cuerpo} desplazable`}>{children}</div>

        {pie && <footer className={estilos.pie}>{pie}</footer>}
      </div>
    </div>
  )
}
